/**
 * Sign in, then unlock.
 *
 * Two separate secrets, deliberately: the password authenticates you to the
 * server, the passphrase decrypts your library. The server verifies the first
 * and never learns the second.
 */

import { useState, type FormEvent } from 'react';

import { WrongPassphrase, useVault } from '../vault';

export function Unlock() {
  const { status, username, progress, signIn, unlock, signOut, itemCount } = useVault();
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * An empty vault has no key to check against, so whatever is typed first
   * silently *becomes* the key. That is a nasty trap when a password manager
   * autofills a stale passphrase: the vault locks itself to a value the user
   * never chose, and every later attempt is rejected as wrong. So when the
   * vault is empty, say plainly that a key is being created and require it
   * twice.
   */
  const creating = itemCount === 0;

  async function submitSignIn(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(user.trim(), password);
      setPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitUnlock(event: FormEvent) {
    event.preventDefault();
    if (creating && passphrase !== confirm) {
      setError('The two passphrases do not match.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await unlock(passphrase);
      setPassphrase('');
      setConfirm('');
    } catch (err) {
      setError(
        err instanceof WrongPassphrase
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err),
      );
    } finally {
      setBusy(false);
    }
  }

  const unlocking = status === 'unlocking';

  return (
    <div className="gate">
      <div className="gate__card">
        <h1 className="gate__logo">peepshow</h1>

        {status === 'signed-out' ? (
          <form onSubmit={submitSignIn}>
            <label className="field">
              <span>Username</span>
              <input
                value={user}
                onChange={(e) => setUser(e.target.value)}
                autoComplete="username"
                autoFocus
                required
              />
            </label>
            <label className="field">
              <span>Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </label>
            <button type="submit" className="btn btn--primary" disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        ) : (
          <form onSubmit={submitUnlock}>
            <p className="gate__who">
              Signed in as <strong>{username}</strong>
            </p>
            {creating && (
              <p className="gate__notice">
                This vault is empty. The passphrase you enter now <strong>becomes</strong> its
                key — check your password manager has not filled in an old one.
              </p>
            )}
            <label className="field">
              <span>{creating ? 'Choose a vault passphrase' : 'Vault passphrase'}</span>
              <input
                type="password"
                value={passphrase}
                onChange={(e) => setPassphrase(e.target.value)}
                // new-password discourages managers from filling a stale value,
                // which is exactly how a vault gets locked to the wrong key.
                autoComplete={creating ? 'new-password' : 'off'}
                autoFocus
                required
                disabled={unlocking}
              />
            </label>
            {creating && (
              <label className="field">
                <span>Confirm passphrase</span>
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  required
                  disabled={unlocking}
                />
              </label>
            )}
            <button type="submit" className="btn btn--primary" disabled={busy || unlocking}>
              {unlocking ? 'Deriving key…' : creating ? 'Create vault key' : 'Unlock'}
            </button>
            {unlocking && progress !== null && (
              <div className="progress">
                <div className="progress__fill" style={{ width: `${Math.round(progress * 100)}%` }} />
              </div>
            )}
            <button type="button" className="btn btn--quiet" onClick={() => void signOut()}>
              Sign out
            </button>
          </form>
        )}

        {error && <p className="gate__error">{error}</p>}

        <p className="gate__fineprint">
          The passphrase never leaves this browser. There is no reset and no recovery: if you
          forget it, the library is gone for good.
        </p>
      </div>
    </div>
  );
}
